<template>
  <div>
    <base-page-template>
      <template #toolbar-items>
        <!-- 編集モード -->
        <template v-if="isEditable">
          <template v-if="isCategory2nd('external')">
            <icon-btn
                tooltip="外部辞書トークン一覧"
                icon="mdi-cog"
                :to="{name: '/edit/tokens/dictionary/DictionaryList'}">
            </icon-btn>
            <icon-btn
                tooltip="トークンからインポート"
                icon="mdi-import"
                @click="openTokenImportDialog">
            </icon-btn>
          </template>
          <template v-else>
            <icon-btn
                tooltip="新規"
                type="add"
                @click="openCreateDialog">
            </icon-btn>
            <icon-btn
                tooltip="CSV からインポート"
                icon="mdi-cloud-upload-outline"
                @click="openCsvImportDialog">
            </icon-btn>
            <icon-btn
                v-if="isCategory2nd('common')"
                tooltip="トークンからインポート"
                icon="mdi-import"
                @click="openTokenImportDialog">
            </icon-btn>
          </template>
        </template>
        <!-- 閲覧モード -->
        <template v-else>
          <icon-btn
              v-if="isCategory2nd('common') && hasPermissions(['dictionary'])"
              tooltip="トークンからインポート"
              icon="mdi-import"
              @click="openTokenImportDialog">
          </icon-btn>
        </template>
        <edit-toggle-button
            component-name="DictionaryList"
            class="with-divider mr-2"
            @change="handleEditToggle">
        </edit-toggle-button>
        <v-toolbar-title class="with-divider">
          {{ dictionaryTypeName }}一覧
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          placeholder="検索"
          clearable
          hide-details
          outlined
          dense
          :style="{width: '300px', flexGrow: 0}">
        </v-text-field>
      </template>

      <template>
        <el-table
            :data="dictionariesView"
            :span-method="buildTableSpan"
            :row-class-name="({row}) => row.header ? 'header-row' : ''"
            row-key="id"
            default-expand-all>
          <el-table-column
              width="300"
              prop="name"
              label="辞書名"
              class-name="name">
            <template #default="{row}">
              <template v-if="row.header">
                <header>{{ row.header }}</header>
              </template>
              <template v-else>
                <v-btn icon class="row-button" @click="toggleStarred(row)" v-if="isCategory2nd('common')">
                  <v-icon color="#f3b500" v-if="isStarredDictionary(row)">mdi-star</v-icon>
                  <v-icon color="grey lighten-1" v-else>mdi-star-outline</v-icon>
                </v-btn>
                <table-inline>
                  <dictionary-name-field
                      :value="row"
                      tooltip-disabled>
                  </dictionary-name-field>
                </table-inline>
              </template>
            </template>
          </el-table-column>
          <el-table-column
              min-width="200"
              prop="description"
              label="説明">
            <template #default="{row}">
              <table-inline>
                <text-field-like
                    :value="row.description"
                    placeholder="未設定"
                    multi-line>
                </text-field-like>
              </table-inline>
            </template>
          </el-table-column>
          <el-table-column
              v-if="isCategory2nd('common')"
              width="200"
              column-key="owner-common"
              label="事業者">
            <template #default="{row}">
              <table-inline>
                <owner-field
                    :value="row.ownerId"
                    :value-handler="owner => row.ownerName = owner.name || ''"
                    hide-details>
                </owner-field>
              </table-inline>
            </template>
          </el-table-column>
          <el-table-column
              v-if="isCategory2nd('external')"
              width="200"
              column-key="owner-external"
              label="事業者">
            <template #default="{row}">
              <table-inline>
                <owner-field
                    v-if="row.sourceDictionary"
                    :value="row.sourceDictionary.ownerId"
                    :value-handler="owner => row.ownerName = owner.name || ''"
                    hide-details>
                </owner-field>
              </table-inline>
            </template>
          </el-table-column>
          <el-table-column
              width="96"
              align="right"
              fixed="right">
            <template #default="{row}">
              <template v-if="!row.header">
                <!-- 削除は共通 -->
                <icon-btn
                    v-if="isEditable"
                    type="delete"
                    @click="openDeleteDialog(row)">
                </icon-btn>

                <!-- トークンでインポートされた共通辞書の利用停止 -->
                <icon-btn
                    v-if="row.dictionaryType === 'common' && row.sourceToken && hasPagePermission(alterRouteName('', 'edit'))"
                    type="disuse"
                    @click="openDisuseDialog(row)">
                </icon-btn>

                <!-- 外部辞書の場合は閲覧のみ -->
                <template v-if="isCategory2nd('external')">
                  <icon-btn
                      type="view"
                      :to="{name: '/browse/external/dictionary/Dictionary', params: {dictionaryId: row.id}}">
                  </icon-btn>
                </template>

                <!-- 共通辞書・個別辞書の場合 -->
                <template v-else>
                  <icon-btn
                      v-if="isEditable"
                      type="edit"
                      :to="{name: alterRouteName('Dictionary'), params: {dictionaryId: row.id}}">
                  </icon-btn>
                  <icon-btn
                      v-else
                      type="view"
                      :to="{name: alterRouteName('Dictionary'), params: {dictionaryId: row.id}}">
                  </icon-btn>
                </template>
              </template>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </base-page-template>

    <!-- 作成ダイアログ -->
    <simple-dialog
        v-model="dialogCreate.visible"
        :title-text="`${dictionaryTypeName}作成`"
        apply-text="次へ"
        @close="closeCreateDialog">
      <dictionary-create-from
        :dictionary-type="category2nd">
      </dictionary-create-from>
    </simple-dialog>

    <!-- 削除ダイアログ -->
    <simple-dialog
        v-model="dialogDelete.visible"
        :title-text="`${dictionaryTypeName}削除`"
        title-color="delete"
        apply-text="削除"
        :header-text="`以下の${dictionaryTypeName}を削除します。本当によろしいですか？`"
        @close="closeDeleteDialog">
      <v-row>
        <v-col cols="12">
          <text-field-like
              v-model="dialogDelete.form.name"
              label="辞書名">
          </text-field-like>
        </v-col>
        <v-col cols="12">
          <text-field-like
              v-model="dialogDelete.form.description"
              label="説明"
              placeholder="未設定"
              multi-line>
          </text-field-like>
        </v-col>
      </v-row>
    </simple-dialog>

    <!-- 利用停止ダイアログ -->
    <simple-dialog
        v-model="dialogDisuse.visible"
        :title-text="`${dictionaryTypeName}利用停止`"
        title-color="delete"
        apply-text="削除"
        :header-text="`以下の${dictionaryTypeName}を利用停止します。本当によろしいですか？`"
        @close="closeDisuseDialog">
      <v-row>
        <v-col cols="12">
          <text-field-like
              v-model="dialogDisuse.form.name"
              label="辞書名">
          </text-field-like>
        </v-col>
        <v-col cols="12">
          <text-field-like
              v-model="dialogDisuse.form.description"
              label="説明"
              placeholder="未設定"
              multi-line>
          </text-field-like>
        </v-col>
        <v-col cols="12">
          <owner-field
              label="作成事業者"
              :value="dialogDisuse.form.ownerId"
              hide-details>
          </owner-field>
        </v-col>
      </v-row>
    </simple-dialog>

    <!-- トークンインポートダイアログ -->
    <simple-dialog
        v-model="dialogTokenImport.visible"
        :title-text="`${dictionaryTypeName}インポート`"
        @close="closeTokenImportDialog">
      <token-import-form
          :dictionary-type="category2nd"
          @after-apply="handleAfterTokenImport">
      </token-import-form>
    </simple-dialog>
  </div>
</template>

<script>
import PathInfoMixin from "@/router/PathInfoMixin";
import IoablePageMixin from "@/router/IoablePageMixin";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import {mapActions, mapGetters, mapMutations} from "vuex";
import query from "@/gql/DictionaryList/Query.gql";
import mutateDictionaryRemove from "@/gql/DictionaryList/DictionaryRemove.gql";
import mutateDictionaryDisuse from "@/gql/DictionaryList/DictionaryDisuse.gql";
import OwnerField from "@/components/molecules/OwnerField.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import BasePageTemplate from "@/components/templates/BasePageTemplate.vue";
import natsort from "natsort";
import EditToggleButton from "@/components/atoms/EditToggleButton.vue";
import IdInfo from "@/store/modules/hds/IdInfo";
import DictionaryNameField from "@/components/molecules/DictionaryNameField.vue";
import IconBtn from "@/components/molecules/IconBtn.vue";
import FileUtils from "@/utils/FileUtils";
import Swal from "sweetalert2";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import PageStoreMixin, {mapPageStores} from "@/utils/PageStoreMixin";
import PermissionMixin from "@/utils/PermissionMixin";
import {DICTIONARY_TYPE_NAMES} from "@/constants";
import TokenImportForm from "@/components/pages/dictionary/DictionaryList/TokenImportForm.vue";
import DictionaryCreateFrom from "@/components/pages/dictionary/DictionaryList/DictionaryCreateFrom.vue";

const sortFn = natsort();

export default {
  name: 'DictionaryList',
  components: {
    DictionaryCreateFrom,
    TokenImportForm,
    TextFieldLike,
    IconBtn,
    DictionaryNameField,
    EditToggleButton,
    BasePageTemplate,
    TableInline,
    OwnerField,
    SimpleDialog
  },
  mixins: [
    PathInfoMixin,
    IoablePageMixin,
    FileUtils,
    PageStoreMixin,
    PermissionMixin,
  ],
  data() {
    return {
      search: '',
      dialogCreate: {
        visible: false,
      },
      dialogDelete: {
        visible: false,
        form: {}
      },
      dialogDisuse: {
        visible: false,
        form: {}
      },
      dialogTokenImport: {
        visible: false,
      },
      starredDictionaries: [],
      starredDictionariesForCategorizing: [],
    };
  },
  computed: {
    ...mapGetters({
      account: 'hcs/account',
      hcsClient: 'hcs/client',
      entities: 'hds/entities',
      hdsClient: 'hds/apiClient',
    }),
    ...mapPageStores({
      dictionaryAction: {alt: 'edit', key: 'dictionary.action'},
    }),
    dictionaryTypeName() {
      return DICTIONARY_TYPE_NAMES[this.category2nd] ?? '辞書';
    },
    dictionaries() {
      return this.entities.dictionary.find();
    },
    dictionariesView() {
      // 表示されるすべての辞書
      const dictionaries = this.dictionaries
          .filter((() => {
            if (this.isAction('edit')) {
              const account = this.$store.getters.account;
              return d => d.ownerId === account.owner?.id;
            } else {
              return () => true;
            }
          })())
          .filter(d => {
            const s = this.search;
            if(s) {
              return d.id.includes(s) ||
                  d.name.includes(s) ||
                  d.description.includes(s) ||
                  d.ownerName?.includes(s)
            }
            return true;
          })
          .sort((a, b) => sortFn(a.name, b.name));

      // 検索中・共通辞書以外の場合はフラットに表示する
      if(this.search || !this.isCategory2nd('common')) {
        return dictionaries;
      }

      // カテゴライズして辞書を表示する
      const categorizedDictionaries = [];
      const starredDictionaries = [];
      const otherDictionaries = dictionaries.filter(d => {
        if (this.isStarredDictionaryForCategorizing(d)) {
          starredDictionaries.push(d);
          return false;
        }
        return true;
      });

      if(starredDictionaries.length) {
        categorizedDictionaries.push({
          id: 'starred',
          header: 'スター付き',
          children: starredDictionaries
        });
      }
      if(otherDictionaries.length) {
        categorizedDictionaries.push({
          id: 'others',
          header: 'その他の辞書',
          children: otherDictionaries
        });
      }

      return categorizedDictionaries;
    }
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    ...mapActions({
      gqlQuery: 'hds/gqlQuery',
      gqlQueryAndLoad: 'hds/gqlQueryAndLoad',
      gqlMutate: 'hds/gqlMutate',
      gqlMutateAndLoad: 'hds/gqlMutateAndLoad'
    }),
    async load() {
      let dictionaryTypes = (() => {
        const type = this.category2nd;
        switch(type) {
          case 'common':
          case 'external':
            return [type];
          case 'local':
            return ['local', 'external']
          default:
            throw new Error(`${type} is not valid dictionaryType.`)
        }
      })();

      if(this.isCategory2nd('common') || this.isCategory2nd('local')) {
        await this.loadStarredDictionaries();
      }

      await this.gqlQueryAndLoad({
        clear: true,
        query,
        variables: {
          dictionaryTypes: dictionaryTypes,
          workingCopy: this.isAction('edit') && !this.isCategory2nd('external')
        },
        mapping: {
          dictionary: 'dictionary',
        }
      });
    },
    async loadStarredDictionaries() {
      const {data: starredDictionaries} = await this.hcsClient.get('/starred_dictionaries');
      this.starredDictionaries = starredDictionaries.slice();
      this.starredDictionariesForCategorizing = starredDictionaries.slice();
    },

    /**
     * テーブルの結合状態を生成します。
     * ヘッダ行の場合に全列を結合します。
     *
     * @param row 行データ
     * @param columnIndex 列インデックス
     * @return {{colspan: number, rowspan: number}} 行・列のセルの結合数
     */
    buildTableSpan({row, columnIndex}) {
      if(columnIndex === 0 && row.header) {
        return {
          rowspan: 1,
          colspan: 4
        };
      }
    },
    findStarredDictionary(dictionary) {
      const elementId = IdInfo.of(dictionary.id).elementId;
      const index = this.starredDictionaries.findIndex(sd => sd.dictionaryId === elementId);
      return {
        dictionaryElementId: elementId,
        index,
        starredDictionary: index !== -1 ? this.starredDictionaries[index] : null
      };
    },
    isStarredDictionary(dictionary) {
      return this.findStarredDictionary(dictionary).index !== -1;
    },
    isStarredDictionaryForCategorizing(dictionary) {
      const elementId = IdInfo.of(dictionary.id).elementId;
      return this.starredDictionariesForCategorizing.findIndex(sd => sd.dictionaryId === elementId) !== -1;
    },
    async toggleStarred(dictionary) {
      const {dictionaryElementId, index: pos} = this.findStarredDictionary(dictionary);
      if(pos === -1) {
        try {
          const {data: newSd} = await this.hcsClient.post('/starred_dictionaries', {dictionaryId: dictionaryElementId});
          this.starredDictionaries.push(newSd);
        } catch (e) {
          await this.loadStarredDictionaries();
        }
        this.showSnackbar({
          message: `辞書 ${dictionary.name} をスターしました`,
        });
      } else {
        const removes = this.starredDictionaries.splice(pos, 1);
        for(const d of removes) {
          await this.hcsClient.delete(`/starred_dictionaries/${d.id}`);
          this.showSnackbar({
            message: `辞書 ${dictionary.name} のスターを解除しました`,
          });
        }
      }
    },

    openCreateDialog() {
      this.dialogCreate.visible = true;
    },
    closeCreateDialog() {
      this.dialogCreate.visible = false;
    },
    openDeleteDialog(row) {
      this.dialogDelete = {
        visible: true,
        form: row
      };
    },
    async closeDeleteDialog(apply) {
      this.dialogDelete.visible = false;
      if (apply) {
        const removeEntity = this.dialogDelete.form;
        try {
          // 表示上すぐ消す
          this.entities.dictionary.remove(removeEntity);

          // スターされていたらスターも取り消す
          const {starredDictionary} = this.findStarredDictionary(removeEntity);
          if(starredDictionary) {
            await this.hcsClient.delete(`/starred_dictionaries/${starredDictionary.id}`);
          }

          // hds からも消す
          await this.gqlMutate({
            mutation: mutateDictionaryRemove,
            variables: {
              removeDictionaries: [removeEntity.id]
            }
          });

          this.showSnackbar({
            message: `${this.dictionaryTypeName}「${removeEntity.name}」を削除しました`
          });
        } catch (e) {
          this.showSnackbar({
            message: `${this.dictionaryTypeName}「${removeEntity.name}」の削除に失敗しました`
          });
        }
      }
    },
    openDisuseDialog(row) {
      this.dialogDisuse = {
        visible: true,
        form: row
      };
    },
    async closeDisuseDialog(apply) {
      const entity = this.dialogDisuse.form;
      this.dialogDisuse.visible = false;
      if (apply) {
        try {
          // 表示上すぐ消す
          this.entities.dictionary.remove(entity);

          // スターされていたらスターも取り消す
          const {starredDictionary} = this.findStarredDictionary(entity);
          if(starredDictionary) {
            await this.hcsClient.delete(`/starred_dictionaries/${starredDictionary.id}`);
          }

          // hds からも消す
          await this.gqlMutate({
            mutation: mutateDictionaryDisuse,
            variables: {
              token: entity.sourceToken
            }
          });

          this.showSnackbar({
            message: `${this.dictionaryTypeName}「${entity.name}」を利用停止しました`
          });
        } catch (e) {
          this.showSnackbar({
            message: `${this.dictionaryTypeName}「${entity.name}」の利用停止に失敗しました`
          });
        }
      }
    },
    openTokenImportDialog() {
      this.dialogTokenImport.visible = true;
    },
    closeTokenImportDialog() {
      this.dialogTokenImport.visible = false;
    },
    handleAfterTokenImport(dictionaryType) {
      if(dictionaryType === 'common') {
        this.$router.push(this.alterRouteName('', this.dictionaryAction = 'browse'));
      }
    },

    async openCsvImportDialog() {
      const file = await this.uploadFile(['application/zip']);
      if(!file) {
        return;
      }

      const loading = this.$loading({
        lock: true,
        text: `辞書をインポートしています...`,
        spinner: 'el-icon-loading',
      });

      try {
        const formData = new FormData();
        formData.append('file', file);
        await this.hdsClient.post('/csv/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });
        loading.close();
        this.showSnackbar({
          message: `辞書をインポートしました`
        });
        await this.load();
      } catch (e) {
        loading.close();
        const message = e.response?.data?.message || e.message;
        await Swal.fire({
          title: '辞書データに不備があります',
          icon: 'warning',
          html: `以下のエラーと共に失敗しました。<p class="error-message">${message}</p>`,
          width: '750px',
          didOpen: _ => {
            Swal.getContent().classList.add('import-dictionary-failed');
          }
        })
      }
    },

    handleEditToggle(action) {
      this.dictionaryAction = action;
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .el-table__indent,
  .el-table__placeholder {
    display: none;
  }

  .row-button {
    vertical-align: middle;
    margin-right: 10px;
  }

  .header-row {
    header {
      margin-left: 10px;
      font-weight: bold;
      display: inline;
    }

    &.hover-row td {
      background-color: inherit;
    }

    & > td:not(:first-child) {
      display: none;
    }
  }

  // 名前列
  td.name > .cell {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
}
</style>

<style>
.import-dictionary-failed p.error-message {
  overflow-y: scroll;
  white-space: pre-wrap;
  font-size: 10pt;
  text-align: left;
  padding: 0.5rem;
  border: solid 1px gray;
  border-radius: 0.2rem;
  margin-top: 1rem;
  min-height: 10rem;
}
</style>
